import { DialogComponent, DialogOpenEvent, DialogCloseEvent } from '@theme/dialog';
import { CartAddEvent } from '@theme/events';
import { isMobileBreakpoint } from '@theme/utilities';

/**
 * A custom element that manages a cart drawer.
 *
 * @typedef {object} Refs
 * @property {HTMLDialogElement} dialog - The dialog element.
 * @property {HTMLElement} [liveRegion] - The live region for cart announcements when dialog is open.
 *
 * @extends {DialogComponent}
 */
class CartDrawerComponent extends DialogComponent {
  /** @type {number} */
  #summaryThreshold = 0.5;

  /** @type {number} */
  #lockedScrollY = 0;

  connectedCallback() {
    super.connectedCallback();
    document.addEventListener(CartAddEvent.eventName, this.#handleCartAdd);
    this.addEventListener(DialogOpenEvent.eventName, this.#updateStickyState);

    // Track scroll in the background passively
    window.addEventListener('scroll', this.#trackScroll, { passive: true });

    if (history.state?.cartDrawerOpen) {
      history.replaceState(null, '');
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener(CartAddEvent.eventName, this.#handleCartAdd);
    this.removeEventListener(DialogOpenEvent.eventName, this.#updateStickyState);
    window.removeEventListener('scroll', this.#trackScroll);
  }

  #trackScroll = () => {
    const isAnyDialogOpen = document.querySelector('dialog[open]');
    
    if (!isAnyDialogOpen) {
      if (window.scrollY === 0 && this.#lockedScrollY > 150) {
        return; 
      }
      this.#lockedScrollY = window.scrollY;
    }
  };

  #scrollDrawerToTop() {
    const scrollContainer = this.querySelector('.cart-drawer__content') || this.refs?.dialog;
    if (scrollContainer) {
      scrollContainer.scrollTop = 0;
    }
  }

  open() {
    this.showDialog();
    this.#scrollDrawerToTop();

    customElements.whenDefined('shopify-payment-terms').then(() => {
      const installmentsContent = document.querySelector('shopify-payment-terms')?.shadowRoot;
      const cta = installmentsContent?.querySelector('#shopify-installments-cta');
      cta?.addEventListener('click', () => this.close(), { once: true });
    });
  }

  close() {
    if (document.activeElement instanceof HTMLElement) {
      document.activeElement.blur();
    }

    const targetScroll = this.#lockedScrollY;
    this.closeDialog();

    const forceScroll = () => {
      if (targetScroll > 0) {
        window.scrollTo({ top: targetScroll, behavior: 'instant' });
      }
    };

    forceScroll();
    requestAnimationFrame(() => {
      forceScroll();
      setTimeout(forceScroll, 10);
      setTimeout(forceScroll, 50);
    });
  }

  /**
   * Handles cart add events - opens drawer if auto-open and announces count when open.
   * @param {CustomEvent<{ resource?: { item_count?: number } }>} event
   */
  #handleCartAdd = (event) => {
    // FIX: Only auto-open the drawer if the suppression flag hasn't been triggered by Quick Add
    if (this.hasAttribute('auto-open') && window._suppressCartDrawer !== true) {
      this.showDialog();
    }

    this.#scrollDrawerToTop();
    this.#announceCartCount(event.detail.resource?.item_count);
  };

  /**
   * Announces cart count to screen readers when dialog is open.
   * @param {number | undefined} cartCount
   */
  #announceCartCount(cartCount) {
    const liveRegion = /** @type {HTMLElement | undefined} */ (this.refs.liveRegion);
    if (!this.refs.dialog?.open || !liveRegion || cartCount === undefined) return;

    liveRegion.textContent = `${Theme.translations.cart_count}: ${cartCount}`;
  }

  #updateStickyState() {
    const { dialog } = /** @type {Refs} */ (this.refs);
    if (!dialog) return;

    const content = dialog.querySelector('.cart-drawer__content');
    const summary = dialog.querySelector('.cart-drawer__summary');

    if (!content || !summary) {
      dialog.setAttribute('cart-summary-sticky', 'false');
      return;
    }

    const drawerHeight = dialog.getBoundingClientRect().height;
    const summaryHeight = summary.getBoundingClientRect().height;
    const ratio = summaryHeight / drawerHeight;
    dialog.setAttribute('cart-summary-sticky', ratio > this.#summaryThreshold ? 'false' : 'true');
  }
}

if (!customElements.get('cart-drawer-component')) {
  customElements.define('cart-drawer-component', CartDrawerComponent);
}